import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Package, Mail, Plus, BarChart, FileText } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    products: 0,
    contacts: 0,
    curriculos: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const [productsRes, contactsRes, curriculosRes] = await Promise.all([
        axios.get(`${API}/products`),
        axios.get(`${API}/contacts`),
        axios.get(`${API}/curriculos`),
      ]);
      setStats({
        products: productsRes.data.length,
        contacts: contactsRes.data.length,
        curriculos: curriculosRes.data.length,
      });
    } catch (error) {
      console.error('Erro ao buscar estatísticas:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Painel Administrativo</h1>
              <p className="text-gray-600 mt-1">Cultivar Agrícola</p>
            </div>
            <Link
              to="/"
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors"
            >
              Voltar ao Site
            </Link>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm font-semibold">Total de Produtos</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{loading ? '...' : stats.products}</p>
              </div>
              <div className="bg-green-100 p-3 rounded-full">
                <Package className="w-8 h-8 text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm font-semibold">Mensagens de Contato</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{loading ? '...' : stats.contacts}</p>
              </div>
              <div className="bg-blue-100 p-3 rounded-full">
                <Mail className="w-8 h-8 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6" data-testid="admin-stat-curriculos">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm font-semibold">Currículos Recebidos</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{loading ? '...' : stats.curriculos}</p>
              </div>
              <div className="bg-purple-100 p-3 rounded-full">
                <FileText className="w-8 h-8 text-purple-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm font-semibold">Sistema</p>
                <p className="text-lg font-bold text-green-600 mt-2">Online</p>
              </div>
              <div className="bg-amber-100 p-3 rounded-full">
                <BarChart className="w-8 h-8 text-amber-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-6">Ações Rápidas</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <Link
              to="/admin/produtos"
              className="flex items-center justify-between p-6 bg-gradient-to-r from-green-50 to-green-100 rounded-lg hover:shadow-md transition-shadow"
            >
              <div>
                <h3 className="font-bold text-gray-900 mb-1">Gerenciar Produtos</h3>
                <p className="text-sm text-gray-600">Adicionar, editar ou remover produtos</p>
              </div>
              <Package className="w-8 h-8 text-green-600" />
            </Link>

            <Link
              to="/admin/produtos/novo"
              className="flex items-center justify-between p-6 bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg hover:shadow-md transition-shadow"
            >
              <div>
                <h3 className="font-bold text-gray-900 mb-1">Novo Produto</h3>
                <p className="text-sm text-gray-600">Adicionar um novo produto ao catálogo</p>
              </div>
              <Plus className="w-8 h-8 text-blue-600" />
            </Link>

            <Link
              to="/admin/contatos"
              className="flex items-center justify-between p-6 bg-gradient-to-r from-purple-50 to-purple-100 rounded-lg hover:shadow-md transition-shadow"
            >
              <div>
                <h3 className="font-bold text-gray-900 mb-1">Mensagens de Contato</h3>
                <p className="text-sm text-gray-600">Ver mensagens recebidas</p>
              </div>
              <Mail className="w-8 h-8 text-purple-600" />
            </Link>

            <Link
              to="/admin/curriculos"
              data-testid="admin-action-curriculos"
              className="flex items-center justify-between p-6 bg-gradient-to-r from-pink-50 to-pink-100 rounded-lg hover:shadow-md transition-shadow"
            >
              <div>
                <h3 className="font-bold text-gray-900 mb-1">Currículos Recebidos</h3>
                <p className="text-sm text-gray-600">Visualizar e baixar candidaturas</p>
              </div>
              <FileText className="w-8 h-8 text-pink-600" />
            </Link>

            <Link
              to="/produtos"
              className="flex items-center justify-between p-6 bg-gradient-to-r from-yellow-50 to-yellow-100 rounded-lg hover:shadow-md transition-shadow"
            >
              <div>
                <h3 className="font-bold text-gray-900 mb-1">Ver Catálogo Público</h3>
                <p className="text-sm text-gray-600">Visualizar como visitante</p>
              </div>
              <BarChart className="w-8 h-8 text-yellow-600" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;